import React from "react";
import CodeBlock from "../components/CodeBlock";
import Analytics, {EVENTS} from "../../../lib/Analytics";

const QuickStart = () => (
  <section id="quick-start" className="developer-section">
    <h2>Quick Start Guide</h2>
    <p>
      Get up and running with the Plex API in four steps. All examples assume
      {" "}<code>https://plexsearch.com</code> as the base URL. You will need an API key
      from your Plex account's Profile &rarr; API Key page.
    </p>
    <p>
      Download the complete Python client:{" "}
      <a href="/api_client.py" download
         onClick={() => Analytics.event(EVENTS.DEVELOPER_DOWNLOAD, {file: "api_client.py"})}>api_client.py</a>
      {" "}(self-contained; includes a customized SSE client with reconnection support).
    </p>

    <h3>Step 1: Verify Connectivity</h3>
    <p>Check that the server is running and get configuration info:</p>
    <CodeBlock
      title="GET /api/version"
      examples={{
        curl: `curl -s /api/version?verbose=true \\
  -H "x-api-key: $PLEX_TOKEN" | jq .`,
        python: `import requests

resp = requests.get(
    "/api/version",
    params={"verbose": "true"},
    headers={"x-api-key": token}
)
info = resp.json()
print(f"Server v{info['version']} - Ready: {info['ready']}")`,
        javascript: `const resp = await fetch(
  "/api/version?verbose=true",
  { headers: { "x-api-key": \`\${token}\` } }
);
const info = await resp.json();
console.log(\`Server v\${info.version} - Ready: \${info.ready}\`);`,
      }}
    />

    <h3>Step 2: Resolve an Entity Name</h3>
    <p>
      Convert a free-text name like "aspirin" into a Plex entity ID. The suggestions
      endpoint returns matching entities with confidence scores:
    </p>
    <CodeBlock
      title="POST /api/suggestions"
      examples={{
        curl: `curl -s /api/suggestions \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '["aspirin"]' | jq '.[] | {id, title: .entity.title, score: ."match-score"}'`,
        python: `resp = requests.post(
    "/api/suggestions",
    headers={"x-api-key": token, "content-type": "application/json"},
    json=["aspirin"]
)
for hit in resp.json():
    print(f"{hit['id']}  {hit['entity']['title']}  (score: {hit.get('match-score', 'n/a')})")
# Output: unichem:161671  Aspirin  (score: 1.0)`,
        javascript: `const resp = await fetch("/api/suggestions", {
  method: "POST",
  headers: {
    "x-api-key": token,
    "Content-Type": "application/json"
  },
  body: JSON.stringify(["aspirin"])
});
const hits = await resp.json();
hits.forEach(h => console.log(\`\${h.id}  \${h.entity.title}  (score: \${h["match-score"]})\`));`,
      }}
    />

    <h3>Step 3: Run a Search</h3>
    <p>
      Use the resolved entity ID to search the knowledge graph. The API supports two
      invocation styles: <strong>polling</strong> (simpler, works everywhere) and{" "}
      <strong>streaming</strong> (real-time progress via SSE). Both return results
      across all categories ranked by relevance.
    </p>

    <h4>Option A: Polling</h4>
    <p>
      Omit <code>stream</code> (or set it to <code>false</code>). The server
      initially returns <code>201</code>, and subsequently returns
      <code>202</code> while the search runs; re-poll every few seconds
      until you receive <code>200</code> with the first page of
      <code>results</code>.
    </p>
    <CodeBlock
      title="POST /api/search (polling)"
      examples={{
        curl: `# Re-POST until status is 200 (202 means still processing)
curl -s /api/search \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"ids": ["unichem:161671"]}' | jq .
# Response while processing: {"status": 202, "progress": 45, "total": 100}
# Final response: {"status": 200, "results": {...}, "totals": {...}}`,
        python: `import time, requests

HEADERS = {"x-api-key": token, "content-type": "application/json"}
body = {"ids": ["unichem:161671"]}

data = {}
while data.get("status") != 200:
    resp = requests.post("/api/search", json=body, headers=HEADERS)
    if resp.status_code >= 400:
        raise ValueError(f"Search failed {resp.status_code}: {resp.content.decode()}")
    data = resp.json()
    if data.get("status") != 200:
        print(f"Status: {data['status']}...")
        time.sleep(5)

for target in data["results"].get("target", []):
    print(f"  {target['rank']:.2f}  {target['entity']['title']}")`,
        javascript: `const headers = { "x-api-key": token, "Content-Type": "application/json" };
const body = JSON.stringify({ ids: ["unichem:161671"] });

let data = {};
while (data.status !== 200) {
  data = await fetch("/api/search", { method: "POST", headers, body }).then(r => r.json());
  if (data.status !== 200) {
    console.log(\`Status: \${data.status}...\`);
    await new Promise(r => setTimeout(r, 5000));
  }
}

data.results.target?.forEach(t =>
  console.log(\`  \${t.rank.toFixed(2)}  \${t.entity.title}\`)
);`,
      }}
    />

    <h4>Option B: Streaming (SSE)</h4>
    <p>
      Pass <code>stream=true</code> as a query parameter. The server responds
      immediately with a <code>stream_id</code>; connect to{" "}
      <code>/api/stream/&#123;stream_id&#125;</code> as a Server-Sent Events
      source to receive progress and results in real time. See the{" "}
      <a href="#sse-streaming">SSE Streaming</a> section for full event details.
    </p>
    <CodeBlock
      title="POST /api/search (streaming)"
      examples={{
        curl: `# 1. Initiate with ?stream=true query param
STREAM_ID=$(curl -s "/api/search?stream=true" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"ids": ["unichem:161671"]}' | jq -r '.stream_id')
# Response: {"stream_id": "STREAM_UUID", "status": 200}

# 2. Connect to the SSE stream
curl -s -N "/api/stream/$STREAM_ID" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Accept: text/event-stream"
# Receives: event: search_update / search_results / search_error`,
        python: `import json, requests
from sseclient import SSEClient  # use the bundled sseclient.py

HEADERS = {"x-api-key": token, "content-type": "application/json"}

# 1. Initiate
init = requests.post(
    "/api/search",
    params={"stream": True},
    headers=HEADERS,
    json={"ids": ["unichem:161671"]}
).json()
stream_id = init["stream_id"]
stream_url = f"/api/stream/{stream_id}"

# 2. Connect and iterate SSE events
sse_resp = requests.get(stream_url,
                        headers={**HEADERS, "Accept": "text/event-stream"},
                        stream=True)
for event in SSEClient(sse_resp, events=["*"], reconnect_url=stream_url).events():
    if event.event == "search_results":
        data = json.loads(event.data)
        for t in data.get("results", {}).get("target", []):
            print(f"  {t['rank']:.2f}  {t['entity']['title']}")
        break`,
        javascript: `// 1. Initiate
const init = await fetch("/api/search?stream=true", {
  method: "POST",
  headers: { "x-api-key": token, "Content-Type": "application/json" },
  body: JSON.stringify({ ids: ["unichem:161671"] })
}).then(r => r.json());

// 2. Connect to SSE stream (token passed as query param for EventSource)
const es = new EventSource(\`/api/stream/\${init.stream_id}?token=\${token}\`);

es.addEventListener("search_update", e => {
  const { progress, total, info } = JSON.parse(e.data);
  console.log(\`Progress: \${progress}/\${total} — \${info}\`);
});

es.addEventListener("search_results", e => {
  const data = JSON.parse(e.data);
  data.results.target?.forEach(t =>
    console.log(\`  \${t.rank.toFixed(2)}  \${t.entity.title}\`)
  );
  es.close();
});

es.addEventListener("search_error", e => {
  console.error("Search failed:", JSON.parse(e.data).error);
  es.close();
});`,
      }}
    />

    <h3>Step 4: Read the Results</h3>
    <p>The response contains results grouped by category. Each result has:</p>
    <ul>
      <li><code>id</code> &mdash; the result entity's ID</li>
      <li><code>rank</code> &mdash; similarity/relevance score (0.0&ndash;1.0)</li>
      <li><code>neighborhood</code> &mdash; community ID this result belongs to in the graph</li>
      <li><code>neighborhood_size</code> &mdash; number of entities in that community</li>
      <li><code>direct_connections</code> &mdash; count of direct edges to your query entities</li>
      <li><code>entity</code> &mdash; full entity data, including <code>title</code> (not <code>name</code>), category, and category-specific fields</li>
    </ul>
    <p>
      Use <code>page_token</code> from the response to fetch the next page of results.
      Use <code>totals</code> to see how many results exist in each category.
    </p>
    <CodeBlock
      title="Paginate results"
      examples={{
        curl: `# Re-POST with page_token from previous response to get the next page
curl -s "/api/search?page_token=CURSOR" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"ids": ["unichem:161671"]}' | jq .`,
        python: `# Collect all pages
body = {"ids": ["unichem:161671"]}
all_targets = []
page_token = None

while True:
    params = {"page_token": page_token} if page_token else {}
    page = requests.post("/api/search", json=body, headers=HEADERS, params=params).json()
    all_targets.extend(page.get("results", {}).get("target", []))
    page_token = page.get("page_token")
    if not page_token:
        break

print(f"Total targets fetched: {len(all_targets)}")`,
        javascript: `// Collect all pages
const body = JSON.stringify({ ids: ["unichem:161671"] });
const allTargets = [];
let pageToken = null;

do {
  const url = pageToken ? \`/api/search?page_token=\${pageToken}\` : "/api/search";
  const page = await fetch(url, { method: "POST", headers, body }).then(r => r.json());
  allTargets.push(...(page.results?.target || []));
  pageToken = page.page_token ?? null;
} while (pageToken);

console.log(\`Total targets fetched: \${allTargets.length}\`);`,
      }}
    />
  </section>
);

export default QuickStart;
